<script setup lang="ts">
/* eslint-disable max-lines */
import {
  EMERGENCY_LABELS,
  RESIDENTIAL_DAMAGE_LABELS_WITHOUT_FLOODING
} from '@/constants/ui/map/legendConstants'
import { useGovernmentSettingStore } from '@/stores/governmentSettingStore'
import { useMapPointCloudStore } from '@/stores/mapPointCloudStore'
import { useRefreshStore } from '@/stores/refreshStore'
import { computed, onMounted, ref, watch } from 'vue'
import { DoughnutChart } from 'vue-chart-3'

const mapPointCloudStore = useMapPointCloudStore()
const refreshStore = useRefreshStore()
const { includesFeatureOptionsEmergency } = useGovernmentSettingStore()

/** チャート用セット */
interface ChartSet {
  /** ラベル */
  labels: string[]
  /** 円グラフ描画用データセット */
  datasets: {
    data: number[]
    backgroundColor: string[]
  }[]
  /** 総件数 */
  allCount: number | null
}

/** 住家被害認定調査用データセット */
const residentialChartData = ref<ChartSet>({
  labels: RESIDENTIAL_DAMAGE_LABELS_WITHOUT_FLOODING.map(e => e.label),
  datasets: [
    {
      data: RESIDENTIAL_DAMAGE_LABELS_WITHOUT_FLOODING.map(() => 0),
      backgroundColor: RESIDENTIAL_DAMAGE_LABELS_WITHOUT_FLOODING.map(e => e.color)
    }
  ],
  allCount: null
})

/** 応急危険度判定用データセット */
const emergencyChartData = ref<ChartSet>({
  labels: EMERGENCY_LABELS.map(e => e.label),
  datasets: [
    {
      data: EMERGENCY_LABELS.map(() => 0),
      backgroundColor: EMERGENCY_LABELS.map(e => e.color)
    }
  ],
  allCount: null
})

/** 住家被害認定調査用チャート描画オプション */
const residentialChartOptions = computed(() => {
  {
    const isAllZeroData = isAllZero(residentialChartData.value.datasets[0].data)
    return {
      cutout: '75%',
      layout: { padding: 30 },
      plugins: {
        legend: { display: false },
        tooltip: { enabled: false },
        showPercentage: !isAllZeroData
      }
    }
  }
})

/** 応急危険度判定用チャート描画オプション */
const emergencyChartOptions = computed(() => {
  {
    const isAllZeroData = isAllZero(emergencyChartData.value.datasets[0].data)
    return {
      cutout: '75%',
      layout: { padding: 30 },
      plugins: {
        legend: { display: false },
        tooltip: { enabled: false },
        showPercentage: !isAllZeroData
      }
    }
  }
})

/** 住家被害認定調査の表示用データ */
const residentialChartDataComputed = computed(() => {
  const isAllZeroData = isAllZero(residentialChartData.value.datasets[0].data)

  return {
    labels: residentialChartData.value.labels,
    datasets: [
      {
        // すべての要素が0件の場合、表示崩れを防ぐために1要素の配列を設定する（値に意味はないが、0以外にする）
        data: isAllZeroData ? [100] : [...residentialChartData.value.datasets[0].data],
        backgroundColor: isAllZeroData
          ? ['#e0e0e0']
          : residentialChartData.value.datasets[0].backgroundColor
      }
    ],
    allCount: residentialChartData.value.allCount,
    _showPercentage: !isAllZeroData
  } as ChartSet & { _showPercentage: boolean }
})

/** 応急危険度判定の表示用データ */
const emergencyChartDataComputed = computed(() => {
  const isAllZeroData = isAllZero(emergencyChartData.value.datasets[0].data)

  return {
    labels: emergencyChartData.value.labels,
    datasets: [
      {
        // すべての要素が0件の場合、表示崩れを防ぐために1要素の配列を設定する（値に意味はないが、0以外にする）
        data: isAllZeroData ? [100] : emergencyChartData.value.datasets[0].data,
        backgroundColor: isAllZeroData
          ? ['#e0e0e0']
          : emergencyChartData.value.datasets[0].backgroundColor
      }
    ],
    allCount: emergencyChartData.value.allCount,
    _showPercentage: !isAllZeroData
  } as ChartSet & { _showPercentage: boolean }
})

/**
 * 対象データがすべて0かどうかを判定する
 * @param data 判定対象データ（リスト）
 */
const isAllZero = (data: number[]): boolean => {
  return data.every(v => v === 0)
}

/**
 * グラフ描画用のデータセットを更新する
 */
const updateDataset = (): void => {
  // 住家被害認定調査の結果をデータセットに反映
  if (mapPointCloudStore.invInspectionResult) {
    residentialChartData.value.datasets[0].data = [
      mapPointCloudStore.invInspectionResult.uninvestigated,
      mapPointCloudStore.invInspectionResult.partiallyDamaged,
      mapPointCloudStore.invInspectionResult.semiPartiallyDestroyed,
      mapPointCloudStore.invInspectionResult.partiallyDestroyed,
      mapPointCloudStore.invInspectionResult.halfTotallyDestroyed,
      mapPointCloudStore.invInspectionResult.almostTotallyDestroyed,
      mapPointCloudStore.invInspectionResult.totallyDestroyed
    ]
    residentialChartData.value.allCount = mapPointCloudStore.invInspectionResult.allCount
  }

  // 応急危険度判定の結果をデータセットに反映
  if (mapPointCloudStore.emergencyResult) {
    emergencyChartData.value.datasets[0].data = [
      mapPointCloudStore.emergencyResult.uninvestigated,
      mapPointCloudStore.emergencyResult.inspected,
      mapPointCloudStore.emergencyResult.warning,
      mapPointCloudStore.emergencyResult.danger
    ]
    emergencyChartData.value.allCount = mapPointCloudStore.emergencyResult.allCount
  }
}

onMounted(async () => {
  // データセット更新
  updateDataset()
})

/**
 * 最新のデータに更新通知受信
 */
watch(
  () => refreshStore.refreshKey,
  () => {
    // データセット更新
    updateDataset()
  }
)
</script>

<template>
  <div class="container">
    <!-- 住家被害認定調査 -->
    <section class="chart-section">
      <h3 class="chart-title">住家被害認定調査</h3>
      <div class="chart-area">
        <!-- 凡例 -->
        <ul class="legend legend-left">
          <li v-for="(label, idx) in residentialChartData.labels" :key="label">
            <span
              class="dot"
              :style="{ backgroundColor: residentialChartData.datasets[0].backgroundColor[idx] }"
            ></span>
            {{ label }}
          </li>
        </ul>
        <!-- グラフ本体 -->
        <div class="graph-wrapper">
          <DoughnutChart
            :chart-data="residentialChartDataComputed"
            :options="residentialChartOptions"
            class="doughnut"
          />
          <div class="center-text">
            {{
              residentialChartDataComputed.allCount
                ? `${residentialChartDataComputed.allCount}棟`
                : 'データなし'
            }}
          </div>
        </div>
      </div>
    </section>

    <!-- 応急危険度判定調査 -->
    <section class="chart-section" v-if="includesFeatureOptionsEmergency()">
      <h3 class="chart-title">応急危険度判定調査</h3>
      <div class="chart-area">
        <!-- 凡例 -->
        <ul class="legend legend-left">
          <li v-for="(label, idx) in emergencyChartData.labels" :key="label">
            <span
              class="dot"
              :style="{ backgroundColor: emergencyChartData.datasets[0].backgroundColor[idx] }"
            ></span>
            {{ label }}
          </li>
        </ul>
        <!-- グラフ本体 -->
        <div class="graph-wrapper">
          <DoughnutChart
            :chart-data="emergencyChartDataComputed"
            :options="emergencyChartOptions"
            class="doughnut"
          />
          <div class="center-text">
            {{
              emergencyChartDataComputed.allCount
                ? `${emergencyChartDataComputed.allCount}棟`
                : 'データなし'
            }}
          </div>
        </div>
      </div>
    </section>
  </div>
</template>

<style scoped>
.container {
  display: flex;
  width: 350px;
  flex-direction: column;
  gap: 30px;
  font-family: 'Noto Sans JP', sans-serif;
  background: rgba(249, 249, 249, 0.95);
  border: 1px solid #ccc;
  margin-left: 0;
}

.chart-section {
  border-top: 1px solid #ccc;
  padding-top: 16px;
  width: 100%;
}

.chart-section:first-of-type {
  border-top: none;
}

.chart-title {
  text-align: center;
  font-weight: 700;
  font-size: 20px;
  margin-bottom: 12px;
}

/* chart-areaは凡例とグラフの横並び */
.chart-area {
  display: flex;
  justify-content: center;
  align-items: center;
}

/* 凡例は左に縦に並べる */
.legend-left {
  list-style: none;
  padding: 0;
  margin: 0;
  width: 130px;
  user-select: none;
  font-size: 13px;
  color: #000;
}

.legend-left li {
  display: flex;
  align-items: center;
  margin-bottom: 6px;
  gap: 8px;
}

.dot {
  display: inline-block;
  width: 14px;
  height: 14px;
}

/* グラフまわりのラッパー */
.graph-wrapper {
  position: relative;
  width: 200px;
  height: 200px;
}

/* ドーナツグラフサイズ */
.doughnut {
  width: 200px;
  height: 200px;
}

/* 中央ラベル */
.center-text {
  position: absolute;
  top: 50%;
  left: 50%;
  transform: translate(-50%, -50%);
  font-weight: 500;
  font-size: 18px;
  user-select: none;
  pointer-events: none;
  color: #222;
}
</style>
